import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImagePlus, Newspaper } from "lucide-react";
import { api } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import PostCard from "@/components/PostCard";
import { Avatar, Empty, EnvTag, Spinner } from "@/components/common";
import { WeatherLine } from "@/components/WeatherLine";
import { BreakingCard, byTime, useBreaking } from "@/components/BreakingCard";
import { LiveUpdateCard, useLiveUpdates } from "@/components/LiveUpdateCard";
import { LiveFeedPicker } from "@/components/LiveFeedPicker";
import { FactActivityCard, useFactActivity } from "@/components/FactActivity";
import { DigestCard } from "@/components/DigestCard";
import { FollowingButton, WhoToFollow } from "@/components/FollowingList";

const FILTERS = [["mine", "My feed"], ["members", "Member Facts"]];
const POLL_MS = 60000;

// Ticks every minute while the tab is visible, and right away when you come back to it.
const useTick = (ms) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => document.visibilityState === "visible" && setTick((t) => t + 1);
    const t = setInterval(bump, ms);
    document.addEventListener("visibilitychange", bump);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", bump); };
  }, [ms]);
  return tick;
};
const HINTS = {
  mine: "Follow Middle Live issues and people who post Facts to fill your feed. Facts you or people you follow react to or comment on show up here too. Find people in Member Facts.",
  members: "No member Facts here yet. Try another subject or filter, or be the first.",
};

export default function Feed() {
  const { me } = useMe();
  const [filter, setFilter] = useState("mine");
  const [posts, setPosts] = useState(null);
  const [cat, setCat] = useState("");
  const [nearby, setNearby] = useState(false);
  const [cats, setCats] = useState([]);
  const mine = filter === "mine";
  useEffect(() => { api.get("/facts/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const tick = useTick(POLL_MS);
  const breaking = useBreaking(tick);
  const live = useLiveUpdates(mine, tick);
  const activity = useFactActivity(mine, tick);
  const actIds = new Set(activity.map((a) => a.id));
  const pinned = [];
  const extras = mine ? [...breaking.items.map((b) => ({ type: "breaking", b, at: [b.at, b.updated_at].filter(Boolean).sort().pop() })),
    ...activity.map((a) => ({ type: "activity", a, at: a.at })),
    ...live.items.filter((u) => !actIds.has(u.id)).map((u) => ({ type: "live", u, at: u.published_at }))] : [];
  const params = { filter, category: mine ? "" : cat, nearby: !mine && nearby };
  const load = () => api.get("/feed", { params }).then((r) => setPosts(r.data));
  // Adds new posts on top without touching the cards already on screen (they keep their own reactions/comments state).
  const merge = () => api.get("/feed", { params }).then((r) => setPosts((xs) => {
    if (!xs) return r.data;
    const have = new Set(xs.map((p) => p.id));
    const add = r.data.filter((p) => !have.has(p.id));
    const unbumped = r.data.filter((p) => !p.at || p.at === p.created_at);
    const oldest = unbumped.length ? unbumped[unbumped.length - 1].created_at : "";
    const fresh = new Map(r.data.map((p) => [p.id, p]));
    const kept = xs.filter((p) => fresh.has(p.id) || p.created_at < oldest);
    const differs = (p, f) => f.at !== p.at || JSON.stringify(f.bumped_by || []) !== JSON.stringify(p.bumped_by || []);
    const moved = kept.some((p) => fresh.has(p.id) && differs(p, fresh.get(p.id)));
    const next = moved ? kept.map((p) => (fresh.has(p.id) && differs(p, fresh.get(p.id)) ? { ...p, at: fresh.get(p.id).at, bumped_by: fresh.get(p.id).bumped_by } : p)) : kept;
    return add.length || moved || kept.length !== xs.length ? [...add, ...next] : xs;
  })).catch(() => {});
  useEffect(() => {
    setPosts(null); load();
    window.addEventListener("middle:feed-refresh", load);
    return () => window.removeEventListener("middle:feed-refresh", load);
  }, [filter, cat, nearby]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tick) merge(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]" data-testid="social-feed-page">
      <div className="min-w-0 space-y-5">
        <div className="space-y-1.5 fade-up">
          <EnvTag env="social" />
          <h1 className="text-3xl font-medium tracking-tight sm:text-4xl">Welcome to your middle ground</h1>
          <p className="text-sm text-slate-500 sm:text-base" data-testid="feed-calm-line">Take a deep breath, everything is going to be fine...</p>
        </div>
        <WeatherLine />
        <Link to="/create/post" data-testid="feed-composer-open" className="surface flex items-center gap-3 p-3.5 transition-colors hover:border-slate-300">
          <Avatar user={me} size={38} />
          <span className="flex-1 text-sm text-slate-400">Share a fact…</span>
          <ImagePlus size={19} className="text-slate-400" />
        </Link>
        <div className="no-scrollbar -mx-4 flex items-center gap-2 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2" role="tablist">
            {FILTERS.map(([k, label]) => (
              <button key={k} role="tab" aria-selected={filter === k} data-testid={`feed-filter-${k}`} onClick={() => setFilter(k)} className={`chip whitespace-nowrap ${filter === k ? "chip-active" : ""}`}>{label}</button>
            ))}
          </div>
          <FollowingButton onChange={() => mine && load()} />
        </div>
        {!mine && <p className="text-xs text-slate-500" data-testid="feed-members-hint">Every member's Fact, newest first, with Middle's check result. Tap Follow to see someone's Facts in your feed.</p>}
        {!mine && <WhoToFollow onFollow={(id) => setPosts((ps) => ps?.map((p) => (p.author_id === id ? { ...p, following_author: true } : p)))} />}
        {!mine && cats.length > 0 && (
          <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:px-0" data-testid="members-topic-filter">
            <button onClick={() => setNearby((v) => !v)} aria-pressed={nearby} data-testid="members-nearby-filter" title="Facts at least 3 members confirmed from within 50 km"
              className={`chip shrink-0 !py-1 text-xs ${nearby ? "chip-active" : ""}`}>Confirmed nearby</button>
            {[["", "All subjects"], ...cats.map((c) => [c.name, c.name])].map(([k, l]) => (
              <button key={k || "all"} onClick={() => setCat(k)} data-testid={`members-cat-${(k || "all").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`chip shrink-0 !py-1 text-xs ${cat === k ? "chip-active" : ""}`}>{l}</button>))}
          </div>)}
        {mine && <LiveFeedPicker following={live.following} onClose={live.reload} />}
        {mine && <DigestCard />}
        {posts === null ? <Spinner /> : posts.length === 0 && !extras.length && !pinned.length ? (
          <Empty title="Nothing here yet" text={HINTS[filter]}>{mine ? <button onClick={() => setFilter("members")} className="text-sm font-medium text-[hsl(var(--social))]" data-testid="feed-empty-members-link">Browse Member Facts</button> : <Link to="/create/post" className="text-sm font-medium text-[hsl(var(--social))]">Share a fact</Link>}</Empty>
        ) : (
          <div className="space-y-4" data-testid="feed-posts">{byTime(posts, pinned, extras).map((x) => x.type === "breaking"
            ? <BreakingCard key={`b-${x.b.id}`} b={x.b} onDismiss={breaking.dismiss} onUnfollow={breaking.unfollow} />
            : x.type === "live" ? <LiveUpdateCard key={`l-${x.u.id}`} u={x.u} />
            : x.type === "activity" ? <FactActivityCard key={`a-${x.a.id}`} f={x.a} />
            : <PostCard key={x.p.id} post={x.p} onRemoved={(id) => setPosts(posts.filter((y) => y.id !== id))} />)}</div>
        )}
      </div>
      <aside className="hidden space-y-5 lg:block">
        <div className="surface p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">How your feed works</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">My feed shows your morning digest, updates from the Middle Live issues you follow, breaking Facts, Facts posted by people you follow, and checked Facts that you or people you follow react to or comment on. Newest first, each with Middle's check result. Member Facts shows Facts from everyone, so you can find people to follow.</p>
          <Link to="/preferences" className="mt-3 inline-block text-sm font-medium text-[hsl(var(--social))] hover:underline" data-testid="feed-preferences-link">Choose your topics</Link>
        </div>
        <Link to="/facts" data-testid="feed-facts-cta" className="surface group block p-5 transition-colors hover:border-emerald-300">
          <Newspaper size={18} className="text-[hsl(var(--facts))]" />
          <p className="mt-3 font-serif text-xl">Want to know what's happening?</p>
          <p className="mt-1 text-sm text-slate-500">Visit Facts for evidence-checked information from approved sources.</p>
        </Link>
      </aside>
    </div>
  );
}
